// /brokers/settlement — who we pay, who we bill, and who is running
// uncovered (SM-799, SM-621 phase 09a).
//
// One row per broker node in GOLD over the chosen period, straight
// from broker-service's settlement position (GET /admin/brokers/
// settlement-positions, BROKER_SETTLEMENT_VIEW) — the console computes
// nothing, so this list, a broker's own page and its portal cannot drift.
// Sorted by uncovered ascending: the most exposed broker first. Notes
// (out-of-band settlements) show as counts in their own column and are
// never added into a figure. The export (BROKER_SETTLEMENT_EXPORT) is the
// same rows, one currency, stating its period.
//
// The net position is information only (SM-878): the intro renders the
// server's caption, and "Uncovered after collateral" is never worded as a
// demand. Each row shows its open payment requests and the root's open
// receivable; a card below lists every request still awaiting payment.
//
// GOLD only (SM-870): every broker node trades GOLD alone, and the one
// node that trades RED and GREEN, the SYSTEM_BROKER, is not on this list.
// An old link's ?currency=RED is dropped rather than obeyed.
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { keepPreviousData, useQuery } from "@tanstack/react-query";

import { FixedCurrency } from "@/components/brokers/FixedCurrency";
import { BROKER_NODE_CURRENCIES } from "@/components/brokers/governance";
import { AwaitingPaymentRequests, Magnitude, SettleAsOf } from "@/components/brokers/SettlementPanels";
import { receivableStateTone } from "@/components/brokers/receivables";
import {
  SETTLEMENT_DEFAULT_DAYS,
  SETTLEMENT_WINDOWS,
  directionLabel,
  directionTone,
  useRenewingSettleWindow,
} from "@/components/brokers/settlement";
import { Badge } from "@/components/ui/Badge";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { EmptyState } from "@/components/ui/EmptyState";
import { ErrorPanel } from "@/components/ui/ErrorPanel";
import { Pagination } from "@/components/ui/Pagination";
import { SegmentedControl } from "@/components/ui/SegmentedControl";
import { Select } from "@/components/ui/Select";
import { Skeleton } from "@/components/ui/Skeleton";
import { useMyPerms } from "@/hooks/useMyPerms";
import { brokerKeys, fetchSettlementPositions, settlementExportURL } from "@/lib/broker-queries";
import type { SettlementPositionListItem, SettlementPositionDirection } from "@/lib/broker-types";
import { cn } from "@/lib/cn";
import { Perm } from "@/lib/perms";

const PAGE_SIZE = 25;
const DIRECTIONS: SettlementPositionDirection[] = ["BROKER_OWES_US", "WE_OWE_BROKER", "SETTLED"];

interface Search {
  days?: number;
  scope?: "node" | "subtree";
  direction?: SettlementPositionDirection;
  sort?: string;
  page?: number;
}

export const Route = createFileRoute("/_app/brokers/settlement/")({
  validateSearch: (s: Record<string, unknown>): Search => ({
    days: SETTLEMENT_WINDOWS.some((w) => w.days === Number(s.days)) ? Number(s.days) : undefined,
    scope: s.scope === "node" ? "node" : undefined,
    direction: DIRECTIONS.includes(s.direction as SettlementPositionDirection) ? (s.direction as SettlementPositionDirection) : undefined,
    sort: s.sort === "-uncovered" || s.sort === "outstanding" || s.sort === "-outstanding" || s.sort === "broker" ? s.sort : undefined,
    page: Number.isFinite(Number(s.page)) && Number(s.page) > 0 ? Number(s.page) : undefined,
  }),
  component: BrokerSettlementPage,
});

const th = "px-3 py-3 text-left text-2xs font-medium uppercase tracking-wide text-muted";
const td = "px-3 py-3 text-xs";

function BrokerSettlementPage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const perms = useMyPerms();
  const canView = perms.has(Perm.BrokerSettlementView);
  const canExport = perms.has(Perm.BrokerSettlementExport);
  const [currency] = BROKER_NODE_CURRENCIES;
  const days = search.days ?? SETTLEMENT_DEFAULT_DAYS;
  const scope = search.scope ?? "subtree";
  const page = search.page ?? 0;
  // A new period, filter or page takes a new window, and so does a return
  // to the tab over a minute later or a page left open for a few minutes
  // (SM-942, SM-958); the export follows it.
  const win = useRenewingSettleWindow(days, `${scope}:${search.direction ?? ""}:${search.sort ?? ""}:${page}`);
  const filter = {
    currency,
    ...win,
    scope,
    direction: search.direction,
    sort: search.sort,
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  };

  const q = useQuery({
    queryKey: brokerKeys.settlementPositions(filter),
    queryFn: () => fetchSettlementPositions(filter),
    placeholderData: keepPreviousData,
    enabled: canView,
  });
  const rows = q.data?.items ?? [];

  if (perms.loaded && !canView) {
    return (
      <EmptyState
        title="No access"
        description="You need the BROKER_SETTLEMENT_VIEW permission to see broker settlement positions."
      />
    );
  }

  const set = (patch: Partial<Search>) => void navigate({ search: (s) => ({ ...s, ...patch, page: undefined }) });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl tracking-tight">Broker settlement</h1>
        <p className="text-sm text-muted">
          Who we pay, who we bill, and who is running uncovered — one currency at a time. Collateral is shown beside the
          position, never inside it.
        </p>
        {q.data?.outstanding_caption ? <p className="text-sm text-muted">{q.data.outstanding_caption}</p> : null}
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div className="w-32">
          <FixedCurrency currency={currency} />
        </div>
        <div className="w-44">
          <Select
            label="Period"
            value={String(days)}
            onChange={(e) => set({ days: Number(e.target.value) === SETTLEMENT_DEFAULT_DAYS ? undefined : Number(e.target.value) })}
            options={SETTLEMENT_WINDOWS.map((w) => ({ value: String(w.days), label: w.label }))}
          />
        </div>
        <div className="w-52">
          <Select
            label="Direction"
            value={search.direction ?? ""}
            onChange={(e) => set({ direction: (e.target.value || undefined) as SettlementPositionDirection | undefined })}
            options={[{ value: "", label: "Every direction" }, ...DIRECTIONS.map((d) => ({ value: d, label: directionLabel(d) }))]}
          />
        </div>
        <div className="w-48">
          <Select
            label="Sort"
            value={search.sort ?? "uncovered"}
            onChange={(e) => set({ sort: e.target.value === "uncovered" ? undefined : e.target.value })}
            options={[
              { value: "uncovered", label: "Most exposed first" },
              { value: "-uncovered", label: "Best covered first" },
              { value: "outstanding", label: "Most owed to us first" },
              { value: "-outstanding", label: "Most owed by us first" },
              { value: "broker", label: "Broker id" },
            ]}
          />
        </div>
        <SegmentedControl<"node" | "subtree">
          label="Figures"
          value={scope}
          onChange={(v) => set({ scope: v === "subtree" ? undefined : v })}
          options={[
            { value: "subtree", label: "Node and its branches" },
            { value: "node", label: "Node alone" },
          ]}
        />
        <div className="ml-auto">
          <SettleAsOf at={q.dataUpdatedAt} fetching={q.isFetching} />
        </div>
        {canExport ? (
          <a
            className="text-2xs text-neon hover:underline"
            href={settlementExportURL(filter)}
            download
          >
            Export CSV for billing
          </a>
        ) : null}
        {/* Its own line under the bar, as on Receivables: the filters
            are bottom-aligned. */}
        <p className="basis-full text-2xs text-muted">
          Only GOLD is listed: every broker node on this list trades GOLD only.
        </p>
      </div>

      <Card>
        <CardHeader className="border-b border-border">
          <CardTitle>Settlement positions</CardTitle>
          <span className="text-2xs text-muted">{q.data?.page.total ?? 0}</span>
        </CardHeader>
        <CardBody className="p-0">
          {q.isLoading || !perms.loaded ? (
            <div className="space-y-2 p-5">
              {[0, 1, 2].map((i) => (
                <Skeleton key={i} className="h-10" />
              ))}
            </div>
          ) : null}
          {q.isError ? <ErrorPanel error={q.error} reset={() => void q.refetch()} /> : null}
          {q.data && rows.length === 0 ? (
            <p className="px-5 py-16 text-center text-sm text-muted">No broker matches these filters.</p>
          ) : null}
          {rows.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-border bg-surface-2">
                  <tr>
                    <th className={`${th} px-5`}>Broker</th>
                    <th className={th}>Direction</th>
                    <th className={`${th} text-right`}>Net position (information only)</th>
                    <th className={`${th} text-right`}>Collateral</th>
                    <th className={`${th} text-right`}>Uncovered after collateral (information only)</th>
                    <th className={`${th} text-right`}>If pending clears</th>
                    <th className={`${th} text-right`}>Gross</th>
                    <th className={`${th} text-right`}>Notes</th>
                    <th className={`${th} text-right`}>Open requests</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((it) => (
                    <SettlementRow
                      key={it.broker_id}
                      it={it}
                      currency={currency}
                      onOpen={() =>
                        void navigate({
                          to: "/brokers/settlement/$brokerId",
                          params: { brokerId: it.broker_id },
                          search: { days: search.days },
                        })
                      }
                    />
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
          {q.data && q.data.page.total > 0 ? (
            <Pagination
              page={q.data.page}
              currentPage={page}
              onPageChange={(p) => void navigate({ search: (s) => ({ ...s, page: p > 0 ? p : undefined }) })}
              itemLabel="brokers"
            />
          ) : null}
        </CardBody>
      </Card>

      <AwaitingPaymentRequests
        onOpen={(brokerId) =>
          void navigate({ to: "/brokers/settlement/$brokerId", params: { brokerId }, search: { days: search.days } })
        }
      />
    </div>
  );
}

function SettlementRow({ it, currency, onOpen }: { it: SettlementPositionListItem; currency: string; onOpen: () => void }) {
  const f = it.figures;
  const uncovered = f.transfer_in_required !== "0";
  return (
    <tr
      className={cn(
        "cursor-pointer border-b border-border/60 last:border-0 hover:bg-surface-2/60",
        uncovered && "bg-danger/5",
      )}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === "Enter") onOpen();
      }}
      tabIndex={0}
    >
      <td className={`${td} px-5`} style={{ paddingLeft: `${1.25 + Math.min(it.broker_depth, 6) * 0.75}rem` }}>
        <div className="font-medium">{it.display_name || it.broker_id}</div>
        <div className="font-mono text-2xs text-muted">{it.broker_id}</div>
        {it.has_descendants ? <div className="text-2xs text-muted">Has branches</div> : null}
      </td>
      <td className={td}>
        {f.has_activity ? (
          <Badge tone={directionTone(f.direction)} className="whitespace-nowrap">{directionLabel(f.direction)}</Badge>
        ) : (
          <span className="text-2xs text-muted">No activity yet</span>
        )}
        {/* A root's unresolved receivable, beside the direction (never in it). */}
        {it.open_receivable ? (
          <div className="mt-1 flex flex-col items-start gap-0.5">
            <Badge tone={receivableStateTone(it.open_receivable.state)} className="whitespace-nowrap">
              Open receivable
            </Badge>
            <Magnitude currency={currency} canon={it.open_receivable.amount} />
          </div>
        ) : null}
      </td>
      <td className={`${td} text-right`}>{f.has_activity ? <Magnitude currency={currency} canon={f.outstanding} strong /> : "—"}</td>
      <td className={`${td} text-right`}>
        <Magnitude currency={currency} canon={f.collateral} />
      </td>
      <td className={cn(`${td} text-right`, uncovered && "font-semibold text-danger")}>
        {uncovered ? (
          <Magnitude currency={currency} canon={f.transfer_in_required} />
        ) : (
          <span className="text-2xs text-muted">Covered</span>
        )}
      </td>
      <td className={`${td} text-right`}>
        {f.pending_note_count > 0 ? (
          <span className="inline-flex flex-col items-end">
            <Magnitude currency={currency} canon={f.outstanding_if_pending_clear} />
            <span className="text-2xs text-muted">{directionLabel(f.if_pending_clear_direction)}</span>
          </span>
        ) : (
          <span className="text-muted">—</span>
        )}
      </td>
      <td className={`${td} text-right`}>
        <span className="inline-flex flex-col items-end">
          <Magnitude currency={currency} canon={f.gross} />
          <span className="text-2xs text-muted">{directionLabel(f.gross_direction)}</span>
        </span>
      </td>
      <td className={`${td} text-right font-mono`}>
        {f.note_count}
        {f.pending_note_count > 0 ? <div className="font-sans text-2xs text-warning">Pending: {f.pending_note_count}</div> : null}
      </td>
      <td className={`${td} text-right`}>
        {!f.payment_requests_open ? (
          <span className="text-muted">—</span>
        ) : f.payment_requests_open.count === 0 ? (
          <span className="font-mono text-muted">0</span>
        ) : (
          <span className="inline-flex flex-col items-end">
            <span className="font-mono">{f.payment_requests_open.count}</span>
            <Magnitude currency={currency} canon={f.payment_requests_open.outstanding_amount} />
          </span>
        )}
      </td>
    </tr>
  );
}
